import { useState, useEffect, useMemo } from 'react';
import {
  useDiagnosticDepartment,
  useDiagnosticDepartments,
  useCreateDiagnosticDepartment,
  useUpdateDiagnosticDepartment,
} from '../../services/httpServices/queries';
import { toast } from 'sonner';
import { Loader2, AlertCircle, Plus, Save, X, Building2, ToggleRight, ToggleLeft, Globe } from 'lucide-react';

interface DiagnosticDepartmentModalProps {
  /** When provided the modal edits that department; otherwise it creates a new one. */
  departmentId?: string | null;
  onClose: () => void;
}

export function DiagnosticDepartmentModal({ departmentId, onClose }: DiagnosticDepartmentModalProps) {
  const isEdit = !!departmentId;

  const { data: deptData, isLoading: deptLoading, error: deptError } = useDiagnosticDepartment(
    departmentId ?? undefined
  );
  const { data: allDeptsData, isLoading: allDeptsLoading } = useDiagnosticDepartments(1, 1000, undefined, true);
  const createMutation = useCreateDiagnosticDepartment();
  const updateMutation = useUpdateDiagnosticDepartment();

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    displayOrder: 0,
    showOnWebsite: true,
  });

  const [codeManuallyEdited, setCodeManuallyEdited] = useState(isEdit);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const department = deptData?.data;
  const allDepartments = allDeptsData?.data || [];

  const displayOrderOptions = useMemo(() => {
    const count = allDepartments.length;
    const options = new Set<number>();
    if (isEdit) {
      options.add(formData.displayOrder);
      for (let i = 1; i <= count; i++) options.add(i);
    } else {
      for (let i = 1; i <= count + 1; i++) options.add(i);
    }
    return Array.from(options).sort((a, b) => a - b);
  }, [allDepartments.length, isEdit, formData.displayOrder]);

  // Auto-generate code from name (new departments only).
  useEffect(() => {
    if (isEdit) return;
    if (!formData.name.trim()) return;
    if (codeManuallyEdited) return;
    const generated = formData.name
      .replace(/[^a-zA-Z0-9]/g, '')
      .slice(0, 3)
      .toUpperCase();
    if (generated) {
      setFormData((prev) => ({ ...prev, code: generated }));
    }
  }, [formData.name, codeManuallyEdited, isEdit]);

  // Default display order for new departments.
  useEffect(() => {
    if (isEdit) return;
    if (allDepartments.length > 0 && formData.displayOrder === 0) {
      setFormData((prev) => ({ ...prev, displayOrder: allDepartments.length + 1 }));
    }
  }, [allDepartments.length, isEdit, formData.displayOrder]);

  // Populate the form when editing.
  useEffect(() => {
    if (isEdit && department) {
      setFormData({
        code: department.code,
        name: department.name,
        description: department.description || '',
        displayOrder: department.displayOrder || 0,
        showOnWebsite: department.showOnWebsite ?? true,
      });
      setCodeManuallyEdited(true);
    }
  }, [isEdit, department]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!formData.code.trim()) errors.code = 'Code is required';
    if (!formData.name.trim()) errors.name = 'Name is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = { ...formData };

    try {
      if (isEdit) {
        await updateMutation.mutateAsync({ id: departmentId!, data: payload });
        toast.success('Department updated');
      } else {
        await createMutation.mutateAsync(payload);
        toast.success('Department created');
      }
      onClose();
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Something went wrong';
      setFormErrors({ general: message });
    }
  };

  const updateField = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === 'code') {
      setCodeManuallyEdited(true);
    }
    if (formErrors[field]) {
      setFormErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const inputBase =
    'w-full px-3 py-2 border rounded-lg bg-background text-text focus:outline-none focus:ring-2 text-sm transition-all';
  const inputValid = 'border-border focus:ring-primary';
  const inputInvalid = 'border-danger focus:ring-danger';
  const labelClass = 'block text-sm font-medium text-text mb-1';

  const ic = (field: string) => `${inputBase} ${formErrors[field] ? inputInvalid : inputValid}`;
  const err = (field: string) =>
    formErrors[field] ? <p className="text-xs text-danger mt-1">{formErrors[field]}</p> : null;

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const loadFailed = isEdit && !deptLoading && (deptError || !department);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-surface border border-border rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 p-4 bg-surface border-b border-border">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="p-2 rounded-lg bg-primary/10 shrink-0">
              <Building2 className="w-4 h-4 text-primary" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-text truncate">
                {isEdit ? 'Edit Department' : 'Add Department'}
              </h2>
              <p className="text-xs text-text-muted truncate">
                {isEdit ? 'Update department details' : 'Create a new hospital department'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-text transition-colors shrink-0"
          >
            <X size={18} />
          </button>
        </div>

        {deptLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : loadFailed ? (
          <div className="flex items-center gap-2 m-4 p-3 bg-danger/10 border border-danger/20 rounded-lg text-danger text-sm">
            <AlertCircle size={16} className="shrink-0" />
            <span>Failed to load department details. Please try again.</span>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            {formErrors.general && (
              <div className="bg-danger/10 text-danger rounded-lg p-3 flex items-center gap-2">
                <AlertCircle size={16} className="shrink-0" />
                <span className="text-sm font-medium">{formErrors.general}</span>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className={labelClass}>Name *</label>
                <input
                  type="text"
                  className={ic('name')}
                  value={formData.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  placeholder="Laboratory"
                  autoFocus
                />
                {err('name')}
              </div>
              <div>
                <label className={labelClass}>
                  Code *{' '}
                  {!isEdit && <span className="text-text-muted font-normal">(auto-generated)</span>}
                </label>
                <input
                  type="text"
                  className={`${ic('code')} font-mono`}
                  value={formData.code}
                  onChange={(e) => updateField('code', e.target.value)}
                  placeholder="LAB"
                />
                {err('code')}
              </div>
              <div>
                <label className={labelClass}>Display Order</label>
                <select
                  className={`${inputBase} ${inputValid}`}
                  value={formData.displayOrder}
                  onChange={(e) => updateField('displayOrder', Number(e.target.value))}
                  disabled={allDeptsLoading}
                >
                  {allDeptsLoading ? (
                    <option>Loading...</option>
                  ) : (
                    displayOrderOptions.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))
                  )}
                </select>
              </div>
            </div>

            <div>
              <label className={labelClass}>Description</label>
              <textarea
                className={`${inputBase} ${inputValid}`}
                rows={3}
                value={formData.description}
                onChange={(e) => updateField('description', e.target.value)}
                placeholder="Optional description of this department"
              />
            </div>

            <div>
              <label className={labelClass}>Website Visibility</label>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => updateField('showOnWebsite', !formData.showOnWebsite)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    formData.showOnWebsite
                      ? 'bg-success/10 text-success border border-success/20'
                      : 'bg-danger/10 text-danger border border-danger/20'
                  }`}
                >
                  {formData.showOnWebsite ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                  {formData.showOnWebsite ? 'Shown on Website' : 'Hidden from Website'}
                </button>
                <p className="text-xs text-text-muted flex items-center gap-1.5">
                  <Globe size={13} className="text-primary shrink-0" />
                  {formData.showOnWebsite
                    ? 'This department will appear on the public website / frontend.'
                    : 'This department will not appear on the public website / frontend.'}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-3 border-t border-border">
              <button
                type="button"
                onClick={onClose}
                disabled={isSaving}
                className="px-4 py-2 text-sm font-medium text-text-muted hover:text-text border border-border rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                <X size={16} />
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-2 shadow-sm"
              >
                {isSaving ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : isEdit ? (
                  <Save size={16} />
                ) : (
                  <Plus size={16} />
                )}
                {isEdit ? 'Save Changes' : 'Add Department'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
